import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import { findChart, getPointIndex, setActiveStepper } from './shared';

/**
 * mood-modal.ts — GRAPH-01 click-to-edit modal for the home Mood graph (Phase 221 Plan 05).
 *
 * Mirrors gym-modal.ts's self-selecting-listeners-into-the-shared-#chart-edit-modal pattern
 * exactly. Renders an emoji-face picker + optional note (ported from the retired Phase-173
 * home-dashboard mood widget markup, D-03) into the shared modal body using DOM APIs only — no raw
 * markup assignment anywhere in this module. The user's own note text is always rendered
 * back via .value/.textContent exclusively (T-221-05-01 self-XSS mitigation). Hides the
 * shared numeric .modal-footer (D-02 — mood has its own Save/note UI, not the bare numeric
 * form), fetches a day's existing log via ?do=getMoodFromDate (edit-mode) or opens blank
 * (add-mode for today, D-04), and saves through the existing ?do=logMood write path so all
 * writes funnel through MoodModel::upsertLog (single source of truth, D-02).
 */

const EMOJI_MAP: Record<number, string> = {
    1: '😞',
    2: '😕',
    3: '😐',
    4: '🙂',
    5: '😄',
};

const LABEL_MAP: Record<number, string> = {
    1: 'Very low',
    2: 'Low',
    3: 'Neutral',
    4: 'Good',
    5: 'Great',
};

// Module state
let currentDate = '';
let selectedScore: number | null = null;
let hiddenListenerAttached = false;
let modalListenersBound = false;

export function initMoodModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            // Defensively restore footer (idempotent — also handled by the other init*Modal fns)
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentDate = '';
            selectedScore = null;
        });
        hiddenListenerAttached = true;
    }

    if (!modalListenersBound) {
        modalListenersBound = true;

        // Autofocus the first emoji face once the modal is actually shown. Self-selects by
        // querying mood's own marker (`.js-mood-face`) — a no-op when a different one of the
        // four modal types is the one currently rendered (Pattern 1).
        modalEl.addEventListener('shown.bs.modal', () => {
            const firstFace = modalEl.querySelector(
                '#chart-edit-modal-body .js-mood-face'
            ) as HTMLButtonElement | null;
            firstFace?.focus();
        });

        // Container-level Enter-to-save. Falls through for <button>/<textarea> targets (face
        // buttons keep their native click behavior; the note textarea keeps native newline
        // entry). Self-selects via the mood-specific Save button (`.js-mood-save` is unique to
        // this modal — no cross-modal class collision) so this listener is a no-op when a
        // different modal type is the one currently rendered.
        modalEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'BUTTON' || target.tagName === 'TEXTAREA') return;
            const saveBtn = modalEl.querySelector('.js-mood-save') as HTMLButtonElement | null;
            if (!saveBtn) return;
            e.preventDefault();
            handleMoodSave(saveBtn);
        });
    }
}

/**
 * Builds the mood form (face picker + note textarea + error slot + Save button) into the given
 * container (Phase 262, HOME-01/02) -- extracted so both the open path's synchronous blank
 * skeleton and the step path's post-fetch rebuild share one implementation. Returns the
 * facesRow/noteArea pair so a caller can prefill them once a getMoodFromDate response resolves.
 */
function buildMoodForm(container: HTMLElement): { facesRow: HTMLElement; noteArea: HTMLTextAreaElement } {
    const facesRow = document.createElement('div');
    facesRow.className = 'd-flex gap-2 align-items-center flex-wrap justify-content-center mb-2';
    facesRow.setAttribute('role', 'group');
    facesRow.setAttribute('aria-label', 'Mood score');

    for (let score = 1; score <= 5; score++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-link p-2 js-mood-face';
        btn.dataset.score = String(score);
        btn.setAttribute('aria-label', 'Score ' + score + ' -- ' + LABEL_MAP[score]);
        btn.setAttribute('aria-pressed', 'false');
        btn.style.fontSize = '1.75rem';
        btn.style.minHeight = '44px';
        btn.style.minWidth = '44px';
        btn.style.lineHeight = '1';
        btn.textContent = EMOJI_MAP[score];
        btn.addEventListener('click', () => {
            selectedScore = score;
            markSelectedFace(facesRow, btn);
        });
        facesRow.appendChild(btn);
    }
    container.appendChild(facesRow);

    // Note textarea — value is set exclusively via .value below (edit-mode prefill), never
    // through raw markup assignment (T-221-05-01: the note is the user's own free text).
    const noteArea = document.createElement('textarea');
    noteArea.className = 'form-control mb-2 js-mood-note';
    noteArea.rows = 3;
    noteArea.maxLength = 280;
    noteArea.placeholder = 'Optional note…';
    noteArea.setAttribute('aria-label', 'Journal note (optional)');
    container.appendChild(noteArea);

    const errorEl = document.createElement('div');
    errorEl.className = 'text-danger small mb-2 d-none js-mood-error';
    container.appendChild(errorEl);

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn btn-primary w-100 js-mood-save';

    const saveLabel = document.createElement('span');
    saveLabel.className = 'js-mood-save-label';
    saveLabel.textContent = 'Save';
    saveBtn.appendChild(saveLabel);

    const spinner = document.createElement('span');
    spinner.className = 'spinner-border spinner-border-sm d-none js-mood-spinner';
    spinner.setAttribute('role', 'status');
    saveBtn.appendChild(spinner);

    saveBtn.addEventListener('click', () => handleMoodSave(saveBtn));
    container.appendChild(saveBtn);

    return { facesRow, noteArea };
}

/**
 * Prefills a built mood form in place from a getMoodFromDate response (Phase 262, HOME-01/02) --
 * shared by both the open path (prefilling the already-visible skeleton) and the step path
 * (prefilling a form just built fresh from the response).
 */
function prefillMoodForm(facesRow: HTMLElement, noteArea: HTMLTextAreaElement, data: any): void {
    if (data.status !== 1 || !data.log) return;   // add-mode — nothing to prefill (D-04)

    const log = data.log;
    const score = parseInt(String(log.score), 10);
    if (score >= 1 && score <= 5) {
        selectedScore = score;
        const faceBtn = facesRow.querySelector(
            '.js-mood-face[data-score="' + score + '"]'
        ) as HTMLButtonElement | null;
        if (faceBtn) markSelectedFace(facesRow, faceBtn);
    }
    if (log.note) {
        noteArea.value = String(log.note);   // .value only — see module header
    }
}

/**
 * Fetch-and-prefill half of openMoodModal() (Phase 262, HOME-01/02) — extracted so the
 * date-nav step path can reload the body for a new date without closing/reopening the modal.
 *
 * Open path (blankUntilLoaded=false): behaves exactly as before — the form is built
 * synchronously (blank, add-mode by default), then getMoodFromDate prefills the SAME nodes in
 * place if a log already exists (never rebuilds, D-04).
 *
 * Step path (blankUntilLoaded=true): the body is cleared and left EMPTY (P-04 — no form) until
 * the fetch resolves, then the whole form is built fresh and prefilled if a log exists.
 *
 * Both paths keep the pre-existing stale-response guard (data.date !== currentDate): a response
 * for a day the user has since navigated away from — via a step OR a direct re-open on another
 * chart point — is dropped rather than clobbering the now-current form's score/note.
 */
function renderBodyForDate(date: string, blankUntilLoaded: boolean, done?: (ok: boolean) => void): void {
    const body = document.getElementById('chart-edit-modal-body');
    if (!body) { done?.(false); return; }
    body.replaceChildren();

    let openFacesRow: HTMLElement | null = null;
    let openNoteArea: HTMLTextAreaElement | null = null;

    if (!blankUntilLoaded) {
        const container = document.createElement('div');
        container.className = 'js-mood-container';
        const built = buildMoodForm(container);
        openFacesRow = built.facesRow;
        openNoteArea = built.noteArea;
        body.appendChild(container);
    }

    get('?do=getMoodFromDate&date=' + date, {}, (data: any) => {
        if (data.date !== currentDate) { done?.(true); return; }   // stale — see doc comment

        if (blankUntilLoaded) {
            const container = document.createElement('div');
            container.className = 'js-mood-container';
            const { facesRow, noteArea } = buildMoodForm(container);
            body.appendChild(container);
            prefillMoodForm(facesRow, noteArea, data);
            done?.(true);
            return;
        }

        prefillMoodForm(openFacesRow!, openNoteArea!, data);
        done?.(true);
    });
}

/**
 * Date-nav step callback (Phase 262, HOME-01/02) -- the ModalDateStepper handed to shared.ts's
 * active-stepper registry from inside openMoodModal().
 */
function step(newDate: string, done: (ok: boolean) => void): void {
    // T-262-30: currentDate is reassigned BEFORE the fetch is issued.
    currentDate = newDate;
    // P-02: stepping discards unsaved edits silently — mirrors openMoodModal's own
    // reset-on-open of selectedScore.
    selectedScore = null;
    renderBodyForDate(newDate, true, done);
}

export function openMoodModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    selectedScore = null;

    // Phase 262 HOME-01/02: register this modal's step implementation before the modal is
    // shown, next to the existing currentDate assignment.
    setActiveStepper(date, step);

    // D-01-style show-first: title/footer are computable synchronously (no field depends on
    // the AJAX response), so render + show BEFORE the getMoodFromDate call. P-01: bare,
    // date-free title — the shared date-nav header owns the date.
    const title = document.getElementById('chart-edit-modal-title');
    if (title) title.textContent = 'Mood';

    const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
    if (footer) footer.classList.add('d-none');   // D-02: mood has its OWN Save/note UI

    // Skeleton renders blank (add-mode) first; renderBodyForDate's GET prefills the
    // already-visible skeleton in place if a log already exists for this day (edit-mode) —
    // same show-first-then-fetch idiom as gym-modal.ts's openGymModal.
    renderBodyForDate(date, false);

    const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
    modalInstance.show();
}

function markSelectedFace(facesRow: HTMLElement, selectedBtn: HTMLButtonElement): void {
    facesRow.querySelectorAll<HTMLButtonElement>('.js-mood-face').forEach(b => {
        b.style.outline = b === selectedBtn ? '2px solid var(--ksat-accent)' : '';
        b.style.borderRadius = b === selectedBtn ? '50%' : '';
        b.setAttribute('aria-pressed', b === selectedBtn ? 'true' : 'false');
    });
}

function handleMoodSave(saveBtn: HTMLButtonElement): void {
    if (saveBtn.disabled) return;   // re-entrancy guard — Enter-to-save calls this directly,
                                     // bypassing native disabled-button click suppression
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    if (!selectedScore) {
        showMoodError(modalEl, 'Please select a mood face.');
        return;
    }

    const noteArea = modalEl.querySelector('.js-mood-note') as HTMLTextAreaElement | null;
    const note = noteArea?.value ?? '';

    const saveLabel = saveBtn.querySelector('.js-mood-save-label') as HTMLElement | null;
    const spinner = saveBtn.querySelector('.js-mood-spinner') as HTMLElement | null;

    saveBtn.disabled = true;
    saveLabel?.classList.add('d-none');
    spinner?.classList.remove('d-none');
    hideMoodError(modalEl);

    // D-02: the single write path — POSTs straight through to handleLogMood, which funnels
    // into MoodModel::upsertLog (1–5 guard + empty-note-to-NULL, single source of truth).
    post('?do=logMood', { day: currentDate, score: selectedScore, note }, (data: any) => {
        saveBtn.disabled = false;
        saveLabel?.classList.remove('d-none');
        spinner?.classList.add('d-none');

        if (data.success) {
            updateMoodChartPoint(currentDate, selectedScore as number);
            const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
            modalInstance.hide();
            notifier.success('Mood saved');
        } else {
            showMoodError(modalEl, data.message || 'Could not save. Please try again.');
        }
    });
}

function showMoodError(modalEl: HTMLElement, msg: string): void {
    const errorEl = modalEl.querySelector('.js-mood-error') as HTMLElement | null;
    if (errorEl) {
        errorEl.textContent = msg;
        errorEl.classList.remove('d-none');
    }
}

function hideMoodError(modalEl: HTMLElement): void {
    const errorEl = modalEl.querySelector('.js-mood-error') as HTMLElement | null;
    if (errorEl) errorEl.classList.add('d-none');
}

function updateMoodChartPoint(date: string, score: number): void {
    const chart = findChart('chart_mood');
    if (!chart) return;
    const series = chart.series[0];
    if (!series) return;
    const idx = getPointIndex(series, date);
    const point = series.data[idx];
    if (point !== undefined) {
        //@ts-ignore
        point.update(score);
    }
}
